import { fixedUi } from '@/i18n/fixedUi';
import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import PlatformProductManagePage from '@/pages/ipe/workbench/PlatformProductManagePage';
import { ProductFilterCard, ProductListCard } from '@/temu-platform/modules/products/components/ProductPageCards';
import TemuOpsListToolbar, { downloadCsv } from '@/temu-platform/components/TemuOpsListToolbar';
import SyncPlatformJsonModal from '@/temu-platform/components/SyncPlatformJsonModal';
import TemuSyncProgressSlot from '@/temu-platform/components/TemuSyncProgressSlot';
import TemuProductSyncModal from '@/temu-platform/components/TemuProductSyncModal';
import BatchQueryModal from '@/temu-platform/modules/products/components/BatchQueryModal';
import {
  BATCH_QUERY_LIMIT,
  joinBatchTokens,
  parseBatchTokens,
} from '@/temu-platform/modules/products/components/batchQueryUtils';
import { useTemuPageShopScope, stackShopDimensionTabs } from '@/temu-platform/hooks/useTemuPageShopScope';
import TemuGatewayRegionTabs, { TEMU_GATEWAY_REGION_ALL } from '@/temu-platform/components/TemuGatewayRegionTabs';
import PlatformShopPickModal from '@/components/platform/PlatformShopPickModal';
import { isAllShopsScope } from '@/utils/platformShopScope';
import { useTemuSyncJobs } from '@/temu-platform/context/TemuSyncJobContext';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { formatSiteListSyncedAt } from '@/temu-platform/utils/temuSiteList';
import { showTemuConfirm } from '@/temu-platform/components/TemuConfirmDialog';
import { fetchAllPages } from '@/temu-platform/utils/temuExportHelpers';
import { buildOpsExportMenu, opsExportLabel } from '@/temu-platform/utils/buildOpsExportMenu';
import { showToast } from '@/utils/toast';
import { readNoticeProductQuery } from '@/temu-platform/utils/temuNoticeFocusQuery';

/**
 * TEMU 导出列。
 *
 * 不含 salePrice / supplyPrice：平台「货品申报价查询」属自研专属接口（ISV 必然 7000022），
 * 「货品列表」响应里也没有价格字段，本地亦无回填值 → 导出只会得到两列空值。
 */
const PRODUCT_EXPORT_HEADERS = [
  'id', 'title', 'platformSpuId', 'platformSkcId', 'platformSkuId',
  'publishStatus', 'priceReviewStatus',
  'stockQty', 'salesLast7Days', 'createdAt', 'publishedAt', 'listedAt', 'lastSyncedAt',
];

export { parseBatchTokens, BATCH_QUERY_LIMIT } from './batchQueryUtils';

const STATUS_TABS = [
  { id: 'all', label: '全部', statKey: 'total' },
  { id: 'listed', label: '已上架', statKey: 'listed' },
  { id: 'pending', label: '待上架', statKey: 'pending' },
  { id: 'sold_out', label: '已售罄', statKey: 'soldOut' },
  { id: 'offline', label: '已下架', statKey: 'offline' },
];

function statusTabCount(stats, tab) {
  if (!stats) return 0;
  // 「全部」用后端按 SKC 去重的总数（货品列表接口一条记录 = 一个 SKC）；
  // 各维度相加会因同一 SKC 的多个 SKU 重复计数
  if (tab.id === 'all') return Number(stats.total) || 0;
  return Number(stats[tab.statKey]) || 0;
}

const ID_TYPE_OPTIONS = [
  { value: 'skc', label: 'SKC' },
  { value: 'spu', label: 'SPU' },
  { value: 'sku', label: 'SKU' },
];

const EMPTY_FILTERS = {
  idType: 'skc',
  idValue: '',
  supplierCode: '',
  brand: '',
};

const EMPTY_QUERY = {
  idType: 'skc',
  keywords: '',
  brand: '',
  supplierCode: '',
};

const BATCH_FIELDS = {
  idValue: {
    titleKey: 'idType',
    titleMap: { skc: '批量查询 SKC', spu: '批量查询 SPU', sku: '批量查询 SKU' },
    placeholder: '每行一个编码，或用逗号分隔',
  },
  supplierCode: {
    title: '批量查询货号',
    placeholder: '每行一个货号，或用逗号分隔',
  },
  brand: {
    title: '批量查询品牌',
    placeholder: '每行一个品牌，或用逗号分隔',
  },
};

/**
 * 产品目录面板
 */
export default function ProductCatalogPanel({
  mode = 'catalog',
  showStatusTabs = true,
}) {
  const {
    activeShopId,
    activeShop,
    loading: shopLoading,
    shopDimensionTabs,
    concreteScopeId,
    allShopsScope,
    pageShopScope,
    scopedShops = [],
    isAllShopsMode,
    setPageShopScope,
  } = useTemuPageShopScope();
  const { isBusy } = useTemuSyncJobs();
  const [searchParams] = useSearchParams();
  const noticeFocusKey = searchParams.toString();
  const [filterDraft, setFilterDraft] = useState(EMPTY_FILTERS);
  const [query, setQuery] = useState(EMPTY_QUERY);
  const [, setQueryNonce] = useState(0);
  const [gatewayRegion, setGatewayRegion] = useState(TEMU_GATEWAY_REGION_ALL);
  const [listingDimension, setListingDimension] = useState(
    mode === 'drafts' ? 'drafts' : mode === 'price_review' ? 'price_review' : mode === 'records' ? 'listed' : 'listed',
  );
  const [dimensionStats, setDimensionStats] = useState(null);
  const [selectedCount, setSelectedCount] = useState(0);
  const selectedIdsRef = useRef([]);
  const selectedSpuIdsRef = useRef([]);
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchModalField, setBatchModalField] = useState(null);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [platformJsonOpen, setPlatformJsonOpen] = useState(false);
  const [syncModalConfirming, setSyncModalConfirming] = useState(false);
  const [shopPickOpen, setShopPickOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const [syncState, setSyncState] = useState({
    refreshing: false,
    listLoading: false,
    listTotal: 0,
    listPageCount: 0,
    cooldownSeconds: 0,
    refresh: null,
    lastRefreshedAt: '',
    exportSelected: null,
    exportPage: null,
    batchShelfOn: null,
    batchShelfOff: null,
    clearSelection: null,
  });

  // 单店 API：页内选「全部」时需先选具体店；具体店用 concreteScopeId
  const listShopId = concreteScopeId;
  const listReady = Boolean(listShopId);

  const productSyncBusy = isBusy('product', listShopId || activeShopId);

  useEffect(() => {
    const notice = readNoticeProductQuery(searchParams);
    const nextDim = mode === 'drafts'
      ? 'drafts'
      : mode === 'price_review'
        ? 'price_review'
        : mode === 'records'
          ? 'listed'
          : (notice?.dim || 'listed');
    setListingDimension(nextDim);
    if (notice?.keywords) {
      setFilterDraft({
        ...EMPTY_FILTERS,
        idType: notice.idType || 'skc',
        idValue: notice.keywords,
      });
      setQueryNonce((n) => {
        const next = n + 1;
        setQuery({
          idType: notice.idType || 'skc',
          keywords: notice.keywords,
          brand: '',
          supplierCode: '',
          _nonce: next,
        });
        return next;
      });
    } else {
      setFilterDraft(EMPTY_FILTERS);
      setQueryNonce((n) => {
        const next = n + 1;
        setQuery({ ...EMPTY_QUERY, _nonce: next });
        return next;
      });
    }
    setSelectedCount(0);
    selectedIdsRef.current = [];
    selectedSpuIdsRef.current = [];
    setBatchModalField(null);
    setPersistedSyncedAt('');
    setGatewayRegion(TEMU_GATEWAY_REGION_ALL);
  }, [mode, listShopId, noticeFocusKey, searchParams]);

  useEffect(() => {
    setSelectedCount(0);
    selectedIdsRef.current = [];
    selectedSpuIdsRef.current = [];
  }, [gatewayRegion]);

  useEffect(() => {
    if (!listShopId) {
      setPersistedSyncedAt('');
      return undefined;
    }
    let cancelled = false;
    temuPlatformAPI.getShopSyncStatus(listShopId)
      .then((res) => {
        if (cancelled) return;
        const modules = Array.isArray(res?.modules) ? res.modules : [];
        const product = modules.find((m) => String(m.module || '').toUpperCase() === 'PRODUCT');
        const at = product?.lastSyncEndAt || product?.lastRunAt || '';
        setPersistedSyncedAt(at ? String(at) : '');
      })
      .catch(() => {
        if (!cancelled) setPersistedSyncedAt('');
      });
    return () => { cancelled = true; };
  }, [listShopId]);

  const latestSyncedAt = syncState.lastRefreshedAt || persistedSyncedAt;
  const latestSyncedLabel = latestSyncedAt
    ? `最近更新：${formatSiteListSyncedAt(latestSyncedAt)}`
    : '尚未同步';

  const onQuery = () => {
    const idTokens = parseBatchTokens(filterDraft.idValue).slice(0, BATCH_QUERY_LIMIT);
    const supplierTokens = parseBatchTokens(filterDraft.supplierCode).slice(0, BATCH_QUERY_LIMIT);
    const brandTokens = parseBatchTokens(filterDraft.brand).slice(0, BATCH_QUERY_LIMIT);
    setSyncState((prev) => ({ ...prev, listLoading: true }));
    setQueryNonce((n) => {
      const next = n + 1;
      setQuery({
        idType: filterDraft.idType || 'skc',
        keywords: idTokens.join(','),
        brand: brandTokens.join(','),
        supplierCode: supplierTokens.join(','),
        _nonce: next,
      });
      return next;
    });
  };

  const onReset = () => {
    setFilterDraft(EMPTY_FILTERS);
    setQueryNonce((n) => {
      const next = n + 1;
      setQuery({ ...EMPTY_QUERY, _nonce: next });
      return next;
    });
    if (mode === 'catalog') setListingDimension('listed');
  };

  const updateField = (key, value) => {
    setFilterDraft((prev) => ({ ...prev, [key]: value }));
  };

  const openBatchModal = (fieldKey) => {
    setBatchModalField(fieldKey);
  };

  const onBatchConfirm = (tokens) => {
    if (!batchModalField) return;
    updateField(batchModalField, joinBatchTokens(tokens));
  };

  const batchModalMeta = batchModalField ? BATCH_FIELDS[batchModalField] : null;
  const batchModalTitle = batchModalMeta
    ? (batchModalMeta.title
      || batchModalMeta.titleMap?.[filterDraft[batchModalMeta.titleKey]]
      || fixedUi("批量查询"))
    : '批量查询';

  // 仅同步展示态；回调走 ref，避免父组件因函数引用变化反复重渲染
  const handleSyncState = useCallback((state) => {
    setSyncState((prev) => {
      const nextLast = state?.lastRefreshedAt || prev.lastRefreshedAt || '';
      const next = {
        ...prev,
        lastRefreshedAt: nextLast,
        refreshing: Boolean(state?.refreshing),
        listLoading: Boolean(state?.listLoading),
        listTotal: Number.isFinite(Number(state?.listTotal)) ? Number(state.listTotal) : prev.listTotal,
        listPageCount: Number.isFinite(Number(state?.listPageCount)) ? Number(state.listPageCount) : prev.listPageCount,
        cooldownSeconds: state?.cooldownSeconds || 0,
        refresh: state?.refresh ?? prev.refresh,
        reloadList: state?.reloadList ?? prev.reloadList,
        exportSelected: state?.exportSelected ?? prev.exportSelected,
        exportPage: state?.exportPage ?? prev.exportPage,
        batchShelfOn: state?.batchShelfOn ?? prev.batchShelfOn,
        batchShelfOff: state?.batchShelfOff ?? prev.batchShelfOff,
        clearSelection: state?.clearSelection ?? prev.clearSelection,
      };
      if (
        next.lastRefreshedAt === prev.lastRefreshedAt
        && next.refreshing === prev.refreshing
        && next.listLoading === prev.listLoading
        && next.listTotal === prev.listTotal
        && next.listPageCount === prev.listPageCount
        && next.cooldownSeconds === prev.cooldownSeconds
        && next.refresh === prev.refresh
        && next.reloadList === prev.reloadList
        && next.exportSelected === prev.exportSelected
        && next.exportPage === prev.exportPage
        && next.batchShelfOn === prev.batchShelfOn
        && next.batchShelfOff === prev.batchShelfOff
        && next.clearSelection === prev.clearSelection
      ) {
        return prev;
      }
      return next;
    });
    if (state?.lastRefreshedAt) {
      setPersistedSyncedAt(String(state.lastRefreshedAt));
    }
  }, []);

  const handleSelectionChange = useCallback((payload) => {
    if (Array.isArray(payload)) {
      selectedIdsRef.current = payload;
      selectedSpuIdsRef.current = [];
      setSelectedCount(payload.length);
      return;
    }
    const ids = Array.isArray(payload?.ids) ? payload.ids : [];
    const spuIds = Array.isArray(payload?.spuIds) ? payload.spuIds : [];
    selectedIdsRef.current = ids;
    selectedSpuIdsRef.current = spuIds;
    const spuCount = Number(payload?.count);
    setSelectedCount(Number.isFinite(spuCount) && spuCount >= 0 ? spuCount : spuIds.length);
  }, []);

  const buildProductListParams = useCallback(() => ({
    shopId: listShopId || undefined,
    priceReviewStatus: 'all',
    publishStatus: 'all',
    listingDimension: mode === 'catalog' ? listingDimension : undefined,
    idType: query?.idType || undefined,
    keywords: query?.keywords || undefined,
    brand: query?.brand || undefined,
    supplierCode: query?.supplierCode || undefined,
    gatewayRegion: gatewayRegion && gatewayRegion !== TEMU_GATEWAY_REGION_ALL ? gatewayRegion : undefined,
  }), [listShopId, listingDimension, mode, query, gatewayRegion]);

  const writeProductsCsv = useCallback((rows, filename) => {
    downloadCsv(
      filename,
      PRODUCT_EXPORT_HEADERS,
      rows.map((row) => ({
        id: row.id,
        title: row.title,
        platformSpuId: row.platformSpuId,
        platformSkcId: row.platformSkcId,
        platformSkuId: row.platformSkuId,
        publishStatus: row.publishStatus,
        priceReviewStatus: row.priceReviewStatus,
        stockQty: row.stockQty,
        salesLast7Days: row.salesLast7Days,
        createdAt: row.createdAt,
        publishedAt: row.publishedAt,
        listedAt: row.listedAt,
        lastSyncedAt: row.lastSyncedAt,
      })),
    );
  }, []);

  const fetchAllProducts = useCallback(async () => {
    const base = buildProductListParams();
    return fetchAllPages(async (page, pageSize) => {
      const res = await temuPlatformAPI.listProducts({ ...base, page, pageSize });
      return {
        items: Array.isArray(res?.items) ? res.items : [],
        total: Number(res?.total) || 0,
      };
    });
  }, [buildProductListParams]);

  const exportFilteredProducts = useCallback(async () => {
    if (!listReady) {
      showToast(allShopsScope ? '请先在店铺维度选择具体店铺' : fixedUi("请先选择店铺"), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const rows = await fetchAllProducts();
      if (!rows.length) {
        showToast(fixedUi("暂无可导出的商品"), 'warning');
        return;
      }
      writeProductsCsv(rows, `temu-products-filtered-${Date.now()}.csv`);
      showToast(`已导出 ${rows.length} 条`, 'success');
    } catch (e) {
      showToast(e?.message || fixedUi("导出筛选失败"), 'error');
    } finally {
      setExporting(false);
    }
  }, [listReady, allShopsScope, exporting, fetchAllProducts, writeProductsCsv]);

  const exportPageProducts = useCallback(() => {
    if (typeof syncState.exportPage === 'function') {
      syncState.exportPage();
      return;
    }
    showToast(fixedUi("当前页没有可导出的商品"), 'warning');
  }, [syncState.exportPage]);

  const exportSelectedProducts = useCallback(() => {
    if (typeof syncState.exportSelected === 'function') {
      syncState.exportSelected();
      return;
    }
    showToast(fixedUi("请先勾选要导出的商品"), 'warning');
  }, [syncState.exportSelected]);

  const listTotal = Number(syncState.listTotal) || 0;
  const listPageCount = Number(syncState.listPageCount) || 0;

  const onBatchDelete = async () => {
    const spuIds = (selectedSpuIdsRef.current || [])
      .map((s) => String(s || '').trim())
      .filter((s) => s && s !== '—');
    if (!listShopId || !spuIds.length) {
      showToast(fixedUi("请先勾选要删除的本地商品（按 SPU）"), 'warning');
      return;
    }
    const ok = await showTemuConfirm({
      title: '批量删除',
      message: `将按 SPU 删除 ${spuIds.length} 个本地商品缓存及其下全部 SKU（仅清本系统，不会删除 TEMU 平台店铺商品）。`,
      tone: 'danger',
      confirmText: '删除',
      cancelText: '取消',
    });
    if (!ok) return;
    setBatchBusy(true);
    try {
      const res = await temuPlatformAPI.batchDeleteProducts(listShopId, { spuNames: spuIds });
      const deletedSpus = Number(res?.deletedSpus);
      const deleted = Number(res?.deleted);
      if (Number.isFinite(deletedSpus) && deletedSpus > 0) {
        showToast(`已删除 ${deletedSpus} 个 SPU（共 ${deleted || 0} 条 SKU 缓存）`, 'success');
      } else if (Number.isFinite(deleted) && deleted > 0) {
        showToast(`已删除 ${deleted} 条本地商品缓存`, 'success');
      } else {
        showToast(fixedUi("未删除任何缓存（可能已删除或不存在）"), 'warning');
      }
      selectedIdsRef.current = [];
      selectedSpuIdsRef.current = [];
      setSelectedCount(0);
      syncState.clearSelection?.();
      // 立刻展示加载动画，再刷新列表与维度数量
      setSyncState((prev) => ({ ...prev, listLoading: true }));
      try {
        await (syncState.reloadList?.() ?? Promise.resolve());
      } finally {
        setSyncState((prev) => ({ ...prev, listLoading: false }));
      }
    } catch (e) {
      showToast(e?.message || '批量删除失败', 'error');
    } finally {
      setBatchBusy(false);
    }
  };

  const statusTabs = useMemo(() => {
    if (!showStatusTabs || mode !== 'catalog') return null;
    return (
      <div className="tpo-status-tabs" role="tablist" aria-label={fixedUi("产品状态")}>
        {STATUS_TABS.map((tab) => {
          const count = statusTabCount(dimensionStats, tab);
          const active = listingDimension === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`tpo-status-tab${active ? ' tpo-status-tab--active' : ''}`}
              onClick={() => {
                startTransition(() => setListingDimension(tab.id));
              }}
            >
              {fixedUi(tab.label)}
              <span className="tpo-status-tab__count">{count}</span>
            </button>
          );
        })}
      </div>
    );
  }, [showStatusTabs, mode, dimensionStats, listingDimension]);

  const gatewayTabs = (
    <TemuGatewayRegionTabs
      shopId={listShopId}
      value={gatewayRegion}
      onChange={(next) => setGatewayRegion(next)}
    />
  );

  const tabs = useMemo(
    () => stackShopDimensionTabs(shopDimensionTabs, (
      <>
        {gatewayTabs}
        {statusTabs}
      </>
    )),
    [shopDimensionTabs, statusTabs, listShopId, gatewayRegion],
  );

  const idTypeLabel = ID_TYPE_OPTIONS.find((o) => o.value === filterDraft.idType)?.label || 'SKC';

  return (
    <>
      <TemuSyncProgressSlot kinds={['product']} />
      <ProductFilterCard
        toolbar={(
          <TemuOpsListToolbar
            selectedCount={selectedCount}
            busy={batchBusy || exporting}
            syncKinds={['product']}
            onSync={() => {
              if (!listReady && !allShopsScope && !isAllShopsMode) {
                showToast(fixedUi("请先在右上角选择已授权的 TEMU 店铺"), 'warning');
                return;
              }
              if (productSyncBusy) {
                showToast(fixedUi("当前店铺正在同步中，请等待同步完成后可操作"), 'warning');
                return;
              }
              if (allShopsScope || isAllShopsMode || isAllShopsScope(pageShopScope)) {
                setShopPickOpen(true);
                return;
              }
              setSyncModalOpen(true);
            }}
            syncing={syncState.refreshing || productSyncBusy}
            syncCooldownSeconds={syncState.cooldownSeconds}
            syncDisabled={shopLoading || productSyncBusy || (!(listReady || allShopsScope || isAllShopsMode)) || (listReady && !syncState.refresh)}
            lastSyncedLabel={latestSyncedLabel}
            syncHint="从 TEMU 后台拉取店铺商品列表与详情，更新到 IPE 本地产品库。"
            trailingExtra={(
              <button
                type="button"
                className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                disabled={!listShopId}
                title={listShopId
                  ? '查看最近一次拉取时平台返回的原始 JSON'
                  : '请先选择具体店铺'}
                onClick={() => setPlatformJsonOpen(true)}
              >
                {fixedUi("平台 JSON")}
              </button>
            )}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!listReady || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: listTotal,
              pageCount: listPageCount,
              selectedCount: selectedCount,
              onExportFiltered: exportFilteredProducts,
              onExportPage: exportPageProducts,
              onExportSelected: exportSelectedProducts,
            })}
            onBatchDelete={onBatchDelete}
            deleteDisabled={!listReady || batchBusy}
            extraActions={[
              {
                key: 'batch-shelf',
                label: selectedCount > 0 ? `批量(${selectedCount})` : '批量',
                disabled: selectedCount === 0,
                menu: [
                  {
                    key: 'shelf-on',
                    label: '上架',
                    count: selectedCount,
                    disabled: selectedCount === 0,
                    onClick: () => {
                      if (!selectedCount) {
                        showToast(fixedUi("请先勾选要上架的商品"), 'warning');
                        return;
                      }
                      syncState.batchShelfOn?.();
                    },
                  },
                  {
                    key: 'shelf-off',
                    label: '下架',
                    count: selectedCount,
                    disabled: selectedCount === 0,
                    onClick: () => {
                      if (!selectedCount) {
                        showToast(fixedUi("请先勾选要下架的商品"), 'warning');
                        return;
                      }
                      syncState.batchShelfOff?.();
                    },
                  },
                ],
              },
            ]}
          />
        )}
      >
        <div className="tpo-filter-bar">
          <div className="tpo-filter-bar__fields">
            <div className="tpo-field tpo-field--inline tpo-field--id-combo tpo-field--batch">
              <select
                className="tpo-select tpo-select--compact"
                value={filterDraft.idType}
                onChange={(e) => updateField('idType', e.target.value)}
                aria-label={fixedUi("编码类型")}
              >
                {ID_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <input
                className="tpo-input"
                value={filterDraft.idValue}
                placeholder={`${idTypeLabel}，支持逗号批量`}
                onChange={(e) => updateField('idValue', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tpo-batch-add-btn"
                aria-label={`批量输入${idTypeLabel}`}
                title={fixedUi("批量查询")}
                onClick={() => openBatchModal('idValue')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>

            <div className="tpo-field tpo-field--inline tpo-field--batch">
              <span className="tpo-field__label">{fixedUi("货号")}</span>
              <input
                className="tpo-input"
                value={filterDraft.supplierCode}
                placeholder={fixedUi("货号，支持逗号批量")}
                onChange={(e) => updateField('supplierCode', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tpo-batch-add-btn"
                aria-label={fixedUi("批量输入货号")}
                title={fixedUi("批量查询")}
                onClick={() => openBatchModal('supplierCode')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>

            <div className="tpo-field tpo-field--inline tpo-field--batch">
              <span className="tpo-field__label">{fixedUi("品牌")}</span>
              <input
                className="tpo-input"
                value={filterDraft.brand}
                placeholder={fixedUi("品牌，支持逗号批量")}
                onChange={(e) => updateField('brand', e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
              <button
                type="button"
                className="tpo-batch-add-btn"
                aria-label={fixedUi("批量输入品牌")}
                title={fixedUi("批量查询")}
                onClick={() => openBatchModal('brand')}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="tpo-filter-bar__actions">
            <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" onClick={onReset}>
              {fixedUi("重置")}
            </button>
            <button type="button" className="tpo-btn tpo-btn--primary tpo-btn--sm" onClick={onQuery}>
              {fixedUi("查询")}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      {productSyncBusy ? (
        <p className="tpo-product-sync-live-hint" role="status">
          商品同步进行中，列表可能稍滞后，可继续查询或其它操作。
        </p>
      ) : null}
      <ProductListCard
        tabs={tabs}
        // 仅列表查询时遮罩；同步中由进度条展示，勿盖「正在查询商品…」
        loading={Boolean(listReady) && syncState.listLoading}
        loadingLabel="正在查询商品…"
      >
        <PlatformProductManagePage
          showToast={showToast}
          embedded
          workspace="temu"
          forcedShopId={listShopId || ''}
          temuExternalToolbar
          opsExternalChrome
          enableTemuOpsToolbar={false}
          hideDimensionTabs
          lockListingDimension={mode !== 'catalog'}
          initialListingDimension={listingDimension}
          controlledListingDimension={listingDimension}
          onListingDimensionChange={setListingDimension}
          controlledQuery={query}
          controlledGatewayRegion={gatewayRegion}
          onDimensionStatsChange={setDimensionStats}
          onSelectionChange={handleSelectionChange}
          onTemuSyncStateChange={handleSyncState}
        />
      </ProductListCard>

      <BatchQueryModal
        open={Boolean(batchModalField)}
        title={batchModalTitle}
        initialValue={batchModalField ? filterDraft[batchModalField] : ''}
        placeholder={batchModalMeta?.placeholder}
        onConfirm={onBatchConfirm}
        onClose={() => setBatchModalField(null)}
      />
      <TemuProductSyncModal
        open={syncModalOpen}
        confirming={syncModalConfirming}
        onClose={() => {
          if (!syncModalConfirming) setSyncModalOpen(false);
        }}
        onConfirm={async (payload) => {
          if (!syncState.refresh) {
            showToast(fixedUi("同步入口未就绪，请稍后重试"), 'warning');
            return;
          }
          setSyncModalConfirming(true);
          try {
            const started = await syncState.refresh(payload);
            if (started !== false) {
              setSyncModalOpen(false);
            }
          } catch (e) {
            showToast(e?.message || '启动商品同步失败', 'error');
          } finally {
            setSyncModalConfirming(false);
          }
        }}
      />
      <SyncPlatformJsonModal
        open={platformJsonOpen}
        shopId={listShopId}
        dumpKind="product"
        title={fixedUi("平台 JSON · 商品")}
        hint="最近一次「拉取 TEMU 后台数据」的原始响应（bg.glo.goods.list.get，未改写）。点编辑时另调的货品详情 bg.glo.goods.detail.get 存在该商品本地缓存里，不在这份列表 dump 中。"
        onClose={() => setPlatformJsonOpen(false)}
      />
      <PlatformShopPickModal
        open={shopPickOpen}
        shops={scopedShops}
        title={fixedUi("选择要同步的店铺")}
        description="全部视图下请选择一家店铺后，再调用 TEMU 平台接口同步。"
        confirmText="继续同步"
        initialShopId={concreteScopeId || ''}
        onClose={() => setShopPickOpen(false)}
        onConfirm={(shopId) => {
          setShopPickOpen(false);
          setPageShopScope(String(shopId));
          setSyncModalOpen(true);
        }}
      />
    </>
  );
}
